import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { authApi } from '@/api/endpoints';

export const Route = createFileRoute('/dashboard/contabilidade')({ component: ContabilidadePage });

/**
 * Doc Contabilidade — guia visível no dashboard do terapeuta.
 *
 * Conteúdo adaptado por categoria:
 *  - PSYCHOLOGIST: MEI vedado; foco em CPF/SLU; reembolso elegível
 *  - THERAPIST:    MEI possível; reembolso só pra controle do cliente
 *
 * Texto baseado em docs/NossoEllo_Guia_Contabilidade_Terapeutas.md.
 * Sempre lembrando: não substitui contador.
 */
function ContabilidadePage() {
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: authApi.me });
  const isPsych = me?.therapistProfile?.professionalCategory === 'PSYCHOLOGIST';
  const isTherapist = me?.therapistProfile?.professionalCategory === 'THERAPIST';

  return (
    <div className="space-y-6 max-w-3xl">
      <header>
        <h1 className="text-2xl font-extrabold text-slate-900">Contabilidade e notas fiscais</h1>
        <p className="text-sm text-slate-500 mt-1">
          Guia rápido sobre emissão de NFSe, regimes tributários e reembolso por convênio.
        </p>
      </header>

      <Disclaimer />

      {/* Estrutura do dinheiro na plataforma — comum às duas categorias */}
      <Section title="1. Como funciona o dinheiro na plataforma">
        <p>
          Em cada atendimento existem <strong>três partes</strong> e <strong>duas notas fiscais</strong>:
        </p>
        <ul className="list-disc ml-6 space-y-1">
          <li><strong>Cliente</strong> paga pelo atendimento.</li>
          <li><strong>Você</strong> presta o serviço e recebe o valor (menos a taxa da plataforma).</li>
          <li><strong>Nosso Ello</strong> retém uma comissão pela intermediação e tecnologia.</li>
        </ul>
        <p>
          A nota da <strong>plataforma → profissional</strong> (sobre a taxa de intermediação) é
          emitida automaticamente no fechamento de cada ciclo. A nota do <strong>profissional →
          cliente</strong> (sobre o atendimento) é gerada apenas quando o cliente solicita,
          para fins de reembolso ou IRPF.
        </p>
      </Section>

      <Section title={isPsych ? '2. Reembolso pelo convênio — você é psicólogo(a)' : '2. Reembolso pelo convênio'}>
        {isPsych ? (
          <>
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-emerald-900">
              <p className="font-semibold mb-1">✓ Seus clientes podem pedir reembolso</p>
              <p className="text-sm">
                Planos de saúde regulados pela ANS reembolsam atendimento psicológico por
                <strong> livre escolha</strong>. Você não precisa ser credenciado a nenhum plano.
              </p>
            </div>
            <p>
              Você pode emitir nota como <strong>pessoa física (CPF)</strong> — basta CRP ativo —
              ou como <strong>pessoa jurídica (CNPJ)</strong>. Em ambos os casos a nota precisa
              conter todos os itens da seção 3.
            </p>
            <p>
              Despesas com psicólogo também são <strong>dedutíveis como despesas médicas</strong> no
              IRPF do cliente (sem limite), reforçando a importância da nota.
            </p>
          </>
        ) : isTherapist ? (
          <>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-900">
              <p className="font-semibold mb-1">ⓘ Reembolso por convênio: em regra, não</p>
              <p className="text-sm">
                Modalidades não regulamentadas (psicanálise, holística, integrativa, floral etc.)
                não são reembolsadas pelos planos de saúde da ANS, porque o reembolso pressupõe
                procedimento e profissional reconhecidos no âmbito da saúde suplementar.
              </p>
            </div>
            <p>
              Seu cliente pode pedir a nota para controle próprio, e eventualmente em benefícios
              corporativos de bem-estar (confirmar com a operadora). Sua nota <strong>não é
              dedutível como despesa médica</strong> no IRPF.
            </p>
          </>
        ) : (
          <p className="text-slate-500 italic">
            Defina sua categoria profissional para ver o conteúdo específico.
          </p>
        )}
      </Section>

      <Section title="3. Dados que a nota precisa conter (para reembolso)">
        <p>Quando o cliente solicita a nota, ela é gerada com:</p>
        <ul className="list-disc ml-6 space-y-1">
          <li>Nome completo e CPF do cliente</li>
          <li>Data da sessão</li>
          <li>Valor por sessão</li>
          <li>Descrição: {isPsych ? '"Sessão de psicoterapia"' : '"Sessão de atendimento terapêutico"'}</li>
          <li>Seu nome completo, {isPsych ? <strong>número do CRP</strong> : 'sua modalidade'} e CPF/CNPJ</li>
        </ul>
        <p className="text-xs text-slate-500">
          Se você emitir a nota fora (sua prefeitura) e fizer upload, garanta que estes dados
          estejam no PDF — sem eles, o convênio costuma recusar.
        </p>
      </Section>

      <Section title="4. Estrutura fiscal — qual a melhor pro seu perfil?">
        {isPsych ? (
          <>
            <p className="bg-rose-50 border border-rose-200 text-rose-900 rounded-lg p-3">
              <strong>Atenção:</strong> Psicólogo(a) <strong>não pode optar pelo MEI</strong>{' '}
              (profissões regulamentadas são vedadas ao MEI por lei).
            </p>
            <Table
              rows={[
                ['Pessoa Física (CPF)', 'Início de carreira, baixo volume', 'IR 7,5–27,5% + INSS + ISS', 'Sim, autônomo direto'],
                ['Microempresa — Simples Nacional (SLU)', 'Faturamento recorrente', '~6% (Anexo III com Fator R)', 'Sim, CNPJ próprio'],
                ['Lucro Presumido', 'Faturamento alto', 'Variável (decidir com contador)', 'Sim'],
              ]}
              headers={['Estrutura', 'Quando usar', 'Carga aproximada', 'Reembolso convênio']}
            />
            <p className="text-xs text-slate-500">
              <strong>Recomendação prática:</strong> comece como autônomo (CPF) se for baixo
              volume; abra CNPJ no Simples Nacional (SLU) quando o faturamento se tornar
              recorrente — cuide do Fator R (folha ≥ 28% do faturamento) para cair no Anexo III.
            </p>
          </>
        ) : (
          <>
            <Table
              rows={[
                ['MEI', 'Se sua ocupação consta na lista MEI atual (confirmar com contador)', 'DAS fixo mensal', 'N/A'],
                ['Pessoa Física (CPF)', 'Baixo volume / início', 'IR 7,5–27,5% + INSS + ISS', 'N/A'],
                ['Microempresa — Simples Nacional', 'Faturamento recorrente', 'Anexo III ou V', 'N/A'],
              ]}
              headers={['Estrutura', 'Quando usar', 'Carga aproximada', 'Reembolso convênio']}
            />
            <p className="text-xs text-slate-500">
              <strong>Recomendação prática:</strong> avalie o MEI se sua ocupação for permitida; caso
              contrário, autônomo (CPF). Avance para microempresa quando o volume crescer.
            </p>
          </>
        )}
      </Section>

      <Section title="5. Checklist prático">
        <ul className="space-y-2 text-sm">
          <li>
            <input type="checkbox" disabled className="mr-2" />
            Definir, com um contador, a estrutura fiscal certa para seu perfil.
          </li>
          {isPsych && (
            <li>
              <input type="checkbox" disabled className="mr-2" />
              Confirmar que seu CRP está ativo.
            </li>
          )}
          <li>
            <input type="checkbox" disabled className="mr-2" />
            Se for emitir como CPF: fazer inscrição municipal de prestador autônomo.
          </li>
          <li>
            <input type="checkbox" disabled className="mr-2" />
            Configurar a emissão de NFSe na sua prefeitura.
          </li>
          <li>
            <input type="checkbox" disabled className="mr-2" />
            Manter as notas organizadas para o seu IRPF (livro-caixa se autônomo).
          </li>
        </ul>
      </Section>

      <FocusNfseStatusBox profile={me?.therapistProfile} />
    </div>
  );
}

/**
 * Doc Contabilidade — só mostra a caixa de "ativar emissão automática" quando
 * o admin já autorizou (focusNfeEnabled=true). Caso contrário, mostra que
 * por enquanto a emissão é manual — não promete uma opção que o terapeuta
 * não consegue ativar sozinho.
 */
function FocusNfseStatusBox({ profile }: { profile?: any }) {
  const enabled = !!profile?.focusNfeEnabled;
  const active = profile?.focusNfeStatus === 'ACTIVE';

  if (enabled && active) {
    return (
      <div className="bg-teal-50 rounded-xl border border-teal-200 p-4 text-sm text-teal-900">
        <p className="font-semibold mb-1">✓ Emissão automática habilitada</p>
        <p>
          Quando seus clientes solicitarem a nota, a Nosso Ello emite automaticamente com os
          dados corretos. Você pode revisar a configuração em{' '}
          <Link to="/dashboard/perfil" className="text-teal-700 underline font-medium">
            seu perfil
          </Link>.
        </p>
      </div>
    );
  }
  if (enabled && !active) {
    return (
      <div className="bg-amber-50 rounded-xl border border-amber-200 p-4 text-sm text-amber-900">
        <p className="font-semibold mb-1">⏳ Configuração em andamento</p>
        <p>
          O administrador autorizou a emissão automática, mas seu cadastro fiscal ainda está
          sendo processado. Enquanto isso, quando seus clientes solicitarem a nota, você
          recebe um email e pode fazer upload do PDF pelo painel.
        </p>
      </div>
    );
  }
  // Não habilitado pelo admin — emissão segue manual
  return (
    <div className="bg-slate-50 rounded-xl border border-slate-200 p-4 text-sm text-slate-600">
      <p className="font-semibold text-slate-700 mb-1">Por enquanto, emissão manual</p>
      <p>
        Quando seu cliente solicitar a nota, você recebe um email com os dados e tem até 7
        dias úteis para emitir pela sua prefeitura e fazer upload do PDF/XML no painel. O
        cliente baixa direto da plataforma.
      </p>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="bg-white rounded-xl border border-slate-200 p-6">
      <h2 className="text-lg font-bold text-slate-800 mb-3">{title}</h2>
      <div className="space-y-3 text-slate-700 leading-relaxed text-sm">{children}</div>
    </section>
  );
}

function Disclaimer() {
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900">
      <strong>Aviso:</strong> este guia é apenas orientativo. <strong>Não substitui a orientação
      de um contador.</strong> Regras tributárias variam por município e por situação individual,
      e mudam com frequência.
    </div>
  );
}

function Table({ headers, rows }: { headers: string[]; rows: string[][] }) {
  return (
    <div className="overflow-x-auto -mx-2">
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr className="bg-slate-50">
            {headers.map((h) => (
              <th key={h} className="text-left font-semibold text-slate-600 p-2 border-b border-slate-200">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-slate-100 last:border-b-0">
              {r.map((c, j) => <td key={j} className="p-2 text-slate-700">{c}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
